import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, Button, Card, CardActions, CardContent, Divider, Grid, IconButton, InputAdornment, Paper, Snackbar, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import Layout from '../components/Layout';

export default function IntegrationsCatalog() {
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [publicKey, setPublicKey] = useState('');
  const [apiBase, setApiBase] = useState('');

  const load = async () => {
    try {
      const c = await apiClient.get('/automations/catalog');
      setCatalog(c.data || []);
    } catch (e) {
      setSnack({ open: true, message: 'Ошибка загрузки каталога', severity: 'error' });
      return;
    }
    try {
      const a = await apiClient.get('/automations');
      setInstalled((a.data || []).map(x => x.templateId));
    } catch (e) {
      // Если нет прав или не авторизованы — просто не подсвечиваем установленные
      setInstalled([]);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        setPublicKey(localStorage.getItem('org_public_key') || '');
      } catch (e) { /* ignore */ }
    }
    // api base можно безопасно взять из клиента или окружения
    try {
      setApiBase((apiClient.defaults?.baseURL || '').replace(/\/$/, ''));
    } catch (e) { setApiBase(''); }
  }, []);

  const genExample = (path, qs = '') => {
    const base = apiBase || '';
    const url = `${base}/public${path}${qs ? `?${qs}` : ''}`;
    return url;
  };

  const copy = async (text) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setSnack({ open: true, message: 'Скопировано', severity: 'success' });
      } else {
        setSnack({ open: true, message: 'Clipboard недоступен', severity: 'error' });
      }
    } catch (e) { setSnack({ open: true, message: 'Не удалось скопировать', severity: 'error' }); }
  };

  const install = async (templateId) => {
    try {
      await apiClient.post('/automations', { templateId });
      setSnack({ open: true, message: 'Сценарий установлен', severity: 'success' });
      load();
    } catch (e) {
      setSnack({ open: true, message: 'Не удалось установить', severity: 'error' });
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Магазин интеграций и сценариев
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Публичные API</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Используйте public_key для выдачи данных на внешние сайты и сервисы. Все ответы — JSON.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Public Key"
              value={publicKey}
              onChange={(e) => { setPublicKey(e.target.value); if (typeof window !== 'undefined') { try { localStorage.setItem('org_public_key', e.target.value); } catch (err) { } } }}
              helperText="Заголовок X-Org-Public-Key или ?public_key="
              sx={{ minWidth: 320 }}
            />
            <TextField
              label="API Base"
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              helperText="Корень API (например, https://api.example.com)"
              sx={{ minWidth: 360 }}
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
            <TextField
              label="Список продуктов"
              value={genExample('/products')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copy(genExample('/products'))}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Поиск по продуктам"
              value={genExample('/documents/products/search', `fields=name,description&q=пакет&public_key=${encodeURIComponent(publicKey)}`)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copy(genExample('/documents/products/search', `fields=name,description&q=пакет&public_key=${encodeURIComponent(publicKey)}`))}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText="Добавьте заголовок X-Org-Public-Key или параметр ?public_key="
            />
            <TextField
              label="Прямая ссылка на файл (бесконечная)"
              value={genExample('/storage/files/<key>/direct', `public_key=${encodeURIComponent(publicKey)}`)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copy(genExample('/storage/files/<key>/direct', `public_key=${encodeURIComponent(publicKey)}`))}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText="Замените <key> на путь к файлу. Ссылка не истекает."
            />
            <TextField
              label="Presigned URL (временная)"
              value={genExample('/storage/files/<key>/url', `public_key=${encodeURIComponent(publicKey)}`)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copy(genExample('/storage/files/<key>/url', `public_key=${encodeURIComponent(publicKey)}`))}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText="Временная ссылка (истекает через час)"
            />
          </Box>
        </Paper>
        <Grid container spacing={2}>
          {(catalog || []).map((item) => (
            <Grid item xs={12} md={4} key={item.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{item.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.description}
                  </Typography>
                  {(item.niches || item.tags) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {(item.niches || []).map((n) => (
                        <Typography key={n} variant="caption" sx={{ bgcolor: '#eef2ff', color: '#3730a3', px: 1, py: 0.25, borderRadius: 1 }}>
                          {n}
                        </Typography>
                      ))}
                      {(item.tags || []).map((t) => (
                        <Typography key={t} variant="caption" sx={{ bgcolor: '#ecfeff', color: '#155e75', px: 1, py: 0.25, borderRadius: 1 }}>
                          #{t}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Триггеры: {(item.triggers || []).join(', ')}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    onClick={() => install(item.id)}
                    disabled={installed.includes(item.id)}
                  >
                    {installed.includes(item.id) ? 'Установлено' : 'Установить в 1 клик'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </Layout>
  );
}


